import dayjs from 'dayjs';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import ColorPicker from '../../../../../components/other/color-picker';
import Portal from '../../../../../components/other/portal';
import SelectWithOtherOption from '../../../../../components/other/select-with-other-option';
import { SCHEDULING_HOURS_OPTIONS, SCHEDULING_MINUTES_OPTIONS, POST_SHIFT_TO_STAFF_PART_OPTIONS } from '../../../../../constants';
import useOutsideClick from '../../../../../hook/useOutsideClick';
import {
  createSchedulingCellAssignmentService,
  deleteSchedulingCellAssignmentService,
  offDueSchedulingCellAssignmentService,
  postShiftCellService,
  unpostShiftCellService,
  updateSchedulingCellAssignmentService,
} from '../../../../../services/scheduling';
import { getDateFromString } from '../../../../../utility';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import DeleteEmployeeModal from '../DeleteEmployeeModal';
import './styles.scss';

const CellAssignmentSidebar = ({
  data,
  handleClose,
  setPeriodData,
  employees,
  assignmentRequestModalRef,
  nurseHours,
  nurseMinutes,
  cnaHours,
  cnaMinutes,
}) => {
  const ref = useRef();
  const deleteEmployeeModalRef = useRef();
  const { roleName, roleId, isPermanentRole, unit, cell, shiftLabel, available, assignment, isPosted, halfPart, isPastPeriod, roleType } =
    data;

  const refsOutsideClick = [ref, deleteEmployeeModalRef];
  if (assignmentRequestModalRef?.current) {
    refsOutsideClick.push(assignmentRequestModalRef);
  }

  const partOfShiftOptions = useMemo(() => {
    if (available == 'both_halves') {
      return [
        { value: 'both_halves', label: 'Full shift' },
        { value: 'first_half', label: 'First half' },
        { value: 'second_half', label: 'Second half' },
      ];
    }
    if (available == 'first_half') {
      return [{ value: 'first_half', label: 'First half' }];
    } else if (available == 'second_half') {
      return [{ value: 'second_half', label: 'Second half' }];
    }
  }, []);

  const offDueToReasonOptions = [
    { value: 'pto', label: 'PTO' },
    { value: 'call_off', label: 'Call off' },
    { value: 'other', label: 'Other' },
  ];

  const getPostedPart = () => {
    if (assignment || halfPart == 'first_half' || halfPart == 'second_half') {
      return null;
    } else if (halfPart == 'both_halves') {
      return POST_SHIFT_TO_STAFF_PART_OPTIONS[0];
    }
  };

  const getOffReason = () => {
    if (assignment?.off_reason) {
      if (offDueToReasonOptions.find(({ value }) => value === assignment.off_reason)) {
        return offDueToReasonOptions.find(({ value }) => value === assignment.off_reason);
      } else {
        return { value: 'other', label: 'Other', otherInputValue: assignment.off_reason };
      }
    }

    return offDueToReasonOptions[0];
  };

  const getBaseHoursAndMinutes = () => {
    if (assignment) {
      return { hours: assignment.hours, minutes: assignment.minutes };
    }
    return roleType === 'nurse' ? { hours: nurseHours, minutes: nurseMinutes } : { hours: cnaHours, minutes: cnaMinutes };
  };

  const getResolvedHoursAndMinutes = () => {
    const { hours, minutes } = getBaseHoursAndMinutes();

    if (halfPart === 'first_half' || halfPart === 'second_half') {
      const halfAllMinutes = (hours * 60 + minutes) / 2;
      const halfHours = Math.floor(halfAllMinutes / 60);
      const halfMinutes = Math.round((halfAllMinutes - halfHours * 60) / 5) * 5;
      return { hours: halfHours, minutes: halfMinutes };
    }

    return { hours, minutes };
  };

  const getInitialHours = () => {
    if (assignment) {
      return SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === assignment.hours);
    }
    const { hours } = getResolvedHoursAndMinutes();
    return SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === hours);
  };

  const getInitialMinutes = () => {
    if (assignment) {
      return SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === assignment.minutes);
    }
    const { minutes } = getResolvedHoursAndMinutes();
    return SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === minutes);
  };
  const initialFormData = {
    employeeFullName: assignment ? assignment.user_name : '',
    employee: assignment?.user ? { value: assignment.user.id, label: assignment.user.full_name } : null,
    post_shift: !!isPosted,
    part_of_shift: partOfShiftOptions.find(({ value }) => value === halfPart) || partOfShiftOptions[0],
    font_color: assignment ? assignment.font_color : '#101828',
    cell_color: assignment ? assignment.cell_color : '#ffffff',
    notes: assignment ? assignment.notes : '',
    off_due: assignment ? assignment.is_off : false,
    off_due_reason: getOffReason(),
    hours: getInitialHours(),
    minutes: getInitialMinutes(),
    posted_part: getPostedPart(),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const employeeOptions = useMemo(() => {
    return employees
      .filter((employee) => (roleType === 'nurse' ? employee.is_nurse : employee.is_cna))
      .map((employee) => ({
        value: employee.id,
        label: employee.full_name,
        position_name: employee.position_name,
      }));
  }, [employees, roleType]);

  useEffect(() => {
    setFormData(initialFormData);
    setFormErrors({});
  }, [halfPart, assignment, data]);

  const handleChangePartOfShift = (e) => {
    const hours = SCHEDULING_HOURS_OPTIONS.find(
      ({ value }) => (roleType === 'nurse' && value === nurseHours) || (roleType === 'cna' && value === cnaHours),
    );
    const minutes = SCHEDULING_MINUTES_OPTIONS.find(
      ({ value }) => (roleType === 'nurse' && value === nurseMinutes) || (roleType === 'cna' && value === cnaMinutes),
    );

    if (e.value === 'both_halves') {
      setFormData((state) => ({
        ...state,
        part_of_shift: e,
        hours: hours,
        minutes: minutes,
      }));
    } else {
      const halfAllMinutes = (hours.value * 60 + minutes.value) / 2;
      const halfHours = Math.floor(halfAllMinutes / 60);
      const halfMinutes = Math.round((halfAllMinutes - halfHours * 60) / 5) * 5;
      setFormData((state) => ({
        ...state,
        part_of_shift: e,
        hours: SCHEDULING_HOURS_OPTIONS.find(({ value }) => value == halfHours),
        minutes: SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value == halfMinutes),
      }));
    }
  };

  const handleEmployeeSelectBlur = () => {
    const formattedNewName = formData.employeeFullName.trim();
    if (formattedNewName.length) {
      setFormData((state) => ({ ...state, employeeFullName: formattedNewName, employee: null }));
    }
  };

  const handleFocusToStringEnd = (e) => {
    const valueLength = e.target.value.length;
    e.target.setSelectionRange(valueLength, valueLength);
  };

  const handleEmployeeSelectKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEmployeeSelectBlur();
      e.preventDefault();
    }
  };

  const onInputChange = (value, action) => {
    if (action.action === 'input-change') {
      setFormData((state) => ({ ...state, employeeFullName: value, employee: null }));
    }
  };

  const handleSelectEmployee = (option) => {
    if (!option) {
      setFormData((state) => ({
        ...state,
        employeeFullName: '',
        employee: null,
        notes: '',
      }));
      return;
    }

    const { value, label, position_name } = option;
    setFormData((state) => ({
      ...state,
      employeeFullName: label,
      employee: { value, label },
      notes: position_name || '',
    }));
  };

  const handlePostShiftToStaffChange = () => {
    if (assignment) {
      if (formData.off_due) {
        setFormData((state) => ({ ...state, post_shift: !state.post_shift }));
      } else {
        if (formData.post_shift) {
          setFormData((state) => ({ ...initialFormData, post_shift: false }));
        } else {
          setFormData((state) => ({ ...state, employee: null, employeeFullName: '', post_shift: true }));
        }
      }
    } else {
      if (!formData.post_shift) {
        setFormData((state) => ({ ...initialFormData, post_shift: true }));
      } else {
        setFormData((state) => ({ ...state, post_shift: false }));
      }
    }
  };

  const handleChangeOffDueTo = () => {
    setFormData((state) => ({
      ...state,
      off_due: !state.off_due,
      cell_color: state.off_due ? '#ffffff' : '#101828',
      font_color: state.off_due ? '#101828' : '#ffffff',
    }));
  };

  const handleDeleteEmployee = useCallback(async () => {
    try {
      const response = await deleteSchedulingCellAssignmentService(assignment.id);
      if (isResponseOk(response)) {
        setPeriodData((prev) => ({
          ...prev,
          units: prev.units.map((unit) => ({
            ...unit,
            roles: unit.roles.map((role) => ({
              ...role,
              cells: role.cells.map((c) => {
                if (
                  c.first_half_user?.id === assignment.id ||
                  c.second_half_user?.id === assignment.id ||
                  c.both_halves_user?.id === assignment.id
                ) {
                  return {
                    ...c,
                    first_half_user: halfPart === 'first_half' ? null : c.first_half_user,
                    second_half_user: halfPart === 'second_half' ? null : c.second_half_user,
                    both_halves_user: halfPart === 'both_halves' ? null : c.both_halves_user,
                  };
                }

                if (assignment?.permanent_cell_assignment?.id) {
                  if (
                    c.first_half_user?.permanent_cell_assignment?.id === assignment.permanent_cell_assignment.id ||
                    c.second_half_user?.permanent_cell_assignment?.id === assignment.permanent_cell_assignment.id ||
                    c.both_halves_user?.permanent_cell_assignment?.id === assignment.permanent_cell_assignment.id
                  ) {
                    return {
                      ...c,
                      first_half_user: halfPart === 'first_half' ? null : c.first_half_user,
                      second_half_user: halfPart === 'second_half' ? null : c.second_half_user,
                      both_halves_user: halfPart === 'both_halves' ? null : c.both_halves_user,
                    };
                  }
                }
                return c;
              }),
            })),
          })),
        }));
        handleClose();
      }
    } catch (err) {
      console.error(err);
    }
  }, [assignment?.id, halfPart, assignment?.permanent_cell_assignment?.id]);

  const handleSave = async () => {
    const payload = {};

    if (formData.off_due) {
      payload.off_due_reason = formData.off_due_reason?.value
        ? formData.off_due_reason.value === 'other'
          ? formData.off_due_reason.otherInputValue
          : formData.off_due_reason.label
        : null;
      payload.post_shift = formData.post_shift;
      payload.notes = formData.notes;

      try {
        const response = await offDueSchedulingCellAssignmentService(assignment.id, payload);

        if (isResponseOk(response)) {
          setPeriodData((prev) => ({
            ...prev,
            days: prev.days.map((day) => {
              return {
                ...day,
                notes: day.id == response.data.day_id ? response.data.notes : day.notes,
              };
            }),
            units: prev.units.map((unit) => ({
              ...unit,
              roles: unit.roles.map((role) => ({
                ...role,
                cells: role.cells.map((c) => (c.id === response.data.cell.id ? response.data.cell : c)),
              })),
            })),
          }));
          handleClose();
        }
      } catch (err) {
        console.error(err);
      }

      return;
    }

    if (formData.post_shift) {
      if (halfPart == 'both_halves') {
        switch (formData.posted_part) {
          case POST_SHIFT_TO_STAFF_PART_OPTIONS[0]:
            payload.half_type = 'both_halves';
            break;
          case POST_SHIFT_TO_STAFF_PART_OPTIONS[1]:
            payload.half_type = 'first_half';
            break;
          case POST_SHIFT_TO_STAFF_PART_OPTIONS[2]:
            payload.half_type = 'second_half';
            break;
          default:
            return;
        }
      } else {
        switch (halfPart) {
          case 'both_halves':
            payload.half_type = 'both_halves';
            break;
          case 'first_half':
            payload.half_type = 'first_half';
            break;
          case 'second_half':
            payload.half_type = 'second_half';
            break;
          default:
            return;
        }
      }
      try {
        const response = await postShiftCellService(cell.id, payload);

        if (isResponseOk(response)) {
          setPeriodData((prev) => ({
            ...prev,
            units: prev.units.map((unit) => ({
              ...unit,
              roles: unit.roles.map((role) => ({
                ...role,
                cells: role.cells.map((c) => (c.id === response.data.id ? response.data : c)),
              })),
            })),
          }));
          handleClose();
        }
      } catch (err) {
        console.error(err);
      }
      return;
    }

    if (isPosted && !formData.post_shift && !formData.employeeFullName) {
      try {
        switch (halfPart) {
          case 'both_halves':
            payload.half_type = 'both_halves';
            break;
          case 'first_half':
            payload.half_type = 'first_half';
            break;
          case 'second_half':
            payload.half_type = 'second_half';
            break;
          default:
            return;
        }

        const response = await unpostShiftCellService(cell.id, payload);

        if (isResponseOk(response)) {
          setPeriodData((prev) => ({
            ...prev,
            units: prev.units.map((unit) => ({
              ...unit,
              roles: unit.roles.map((role) => ({
                ...role,
                cells: role.cells.map((c) => (c.id === response.data.id ? response.data : c)),
              })),
            })),
          }));
          handleClose();
        }
      } catch (err) {
        console.error(err);
      }
      return;
    }

    Object.assign(payload, {
      scheduling_cell_id: cell.id,
      user: formData.employee?.value,
      user_name: formData.employeeFullName,
      type: formData.part_of_shift.value,
      font_color: formData.font_color,
      cell_color: formData.cell_color,
      hours: formData.hours.value,
      minutes: formData.minutes.value,
      notes: formData.notes || '',
    });

    let service = assignment ? updateSchedulingCellAssignmentService : createSchedulingCellAssignmentService;
    let params = assignment ? [assignment.id, payload] : [payload];

    try {
      const response = await service(...params);

      if (isResponseOk(response)) {
        setPeriodData((prev) => ({
          ...prev,
          units: prev.units.map((unit) => ({
            ...unit,
            roles: unit.roles.map((role) => (role.id === roleId ? response.data : role)),
          })),
        }));
        handleClose();
      } else {
        setFormErrors(response.data);
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setFormErrors(err.response.data);
      }
    }
  };

  const isCellFromPast = useMemo(() => {
    if (cell.day) {
      const cellDate = getDateFromString(cell.day);
      return cellDate.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
    }
    return false;
  }, [cell.day]);

  const renderColorFields = () => {
    return (
      <>
        <div className="form-item form-color-wrapper">
          <div className="form-font-color">
            <ColorPicker
              color={formData.font_color}
              isDisabled={formData.off_due}
              onChange={(color) => setFormData((state) => ({ ...state, font_color: color.hex }))}
              label="Font color"
              pickerWidth="342px"
            />
          </div>
          <div className="form-font-color">
            <ColorPicker
              isDisabled={formData.off_due}
              color={formData.cell_color}
              onChange={(color) => setFormData((state) => ({ ...state, cell_color: color.hex }))}
              label="Cell color"
              pickerWidth="342px"
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <Portal>
      <div className="cell-assignment-sidebar" ref={ref}>
        <div className="cell-assignment-sidebar-header">
          <div className="cell-assignment-sidebar-header-title-wrapper">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="32" height="32" rx="16" fill="#F4EBFF" />
              <rect x="2" y="2" width="32" height="32" rx="16" stroke="#F9F5FF" strokeWidth="4" />
              <path
                d="M23.3332 24V22.6667C23.3332 21.9594 23.0522 21.2811 22.5521 20.781C22.052 20.281 21.3737 20 20.6665 20H15.3332C14.6259 20 13.9476 20.281 13.4476 20.781C12.9475 21.2811 12.6665 21.9594 12.6665 22.6667V24M20.6665 14.6667C20.6665 16.1394 19.4726 17.3333 17.9998 17.3333C16.5271 17.3333 15.3332 16.1394 15.3332 14.6667C15.3332 13.1939 16.5271 12 17.9998 12C19.4726 12 20.6665 13.1939 20.6665 14.6667Z"
                stroke="#7F56D9"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {unit.unit.name}, {roleName}
          </div>
          <button className="btn btn-icon" onClick={handleClose}>
            <i className="icon-icon-x"></i>
          </button>
        </div>
        <div className="cell-assignment-sidebar-subheader">
          {dayjs(cell.day).format('MM/DD dddd')}, shift {shiftLabel}
        </div>
        <div className="cell-assignment-sidebar-body">
          <div className="cell-assignment-form">
            <div className="form-item">
              <label className="form-label">Name Last Name</label>
              <div className="sidebar-name-select-wrapper">
                <Select
                  isSearchable
                  options={employeeOptions}
                  isClearable
                  isDisabled={formData.post_shift || formData.off_due}
                  inputValue={formData.employee ? formData.employee.label : formData.employeeFullName}
                  className="form-control-select"
                  classNamePrefix="react-select"
                  value={formData.employee}
                  styles={{
                    input: (provided) => ({
                      ...provided,
                      input: {
                        opacity: '1 !important',
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      minWidth: '100%',
                    }),
                    option: (provided) => ({
                      ...provided,
                      cursor: 'pointer',
                    }),
                  }}
                  placeholder="Search or add manually "
                  onFocus={handleFocusToStringEnd}
                  onKeyDown={handleEmployeeSelectKeyDown}
                  onInputChange={onInputChange}
                  onChange={handleSelectEmployee}
                  noOptionsMessage={(e) => null}
                />
                {(formData.employeeFullName || formData.employee?.label) && assignment && (
                  <button
                    className="btn delete-assignment-btn"
                    onClick={() => setDeleteEmployeeId(assignment.id)}
                    disabled={formData.off_due}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M2.5 4.9974H4.16667M4.16667 4.9974H17.5M4.16667 4.9974V16.6641C4.16667 17.1061 4.34226 17.53 4.65482 17.8426C4.96738 18.1551 5.39131 18.3307 5.83333 18.3307H14.1667C14.6087 18.3307 15.0326 18.1551 15.3452 17.8426C15.6577 17.53 15.8333 17.1061 15.8333 16.6641V4.9974H4.16667ZM6.66667 4.9974V3.33073C6.66667 2.8887 6.84226 2.46478 7.15482 2.15222C7.46738 1.83966 7.89131 1.66406 8.33333 1.66406H11.6667C12.1087 1.66406 12.5326 1.83966 12.8452 2.15222C13.1577 2.46478 13.3333 2.8887 13.3333 3.33073V4.9974M8.33333 9.16406V14.1641M11.6667 9.16406V14.1641"
                        stroke="white"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <span className="form-error">{formErrors.user ? formErrors.user[0] : ''}</span>
            </div>

            {(formData.employeeFullName || formData.employee?.label) && (
              <>
                <div className="form-items-wrapper">
                  <div className="form-item">
                    <label className="form-label">Full or part shift</label>
                    <Select
                      isSearchable={false}
                      options={partOfShiftOptions}
                      className="form-control-select"
                      classNamePrefix="react-select"
                      placeholder="Part of the shift"
                      filterOption={null}
                      isDisabled={formData.off_due}
                      value={formData.part_of_shift}
                      onChange={(e) => handleChangePartOfShift(e)}
                    />
                  </div>
                </div>
                <div className="form-items-wrapper">
                  <div className="form-item">
                    <label className="form-label">Working hours</label>
                    <Select
                      isSearchable={false}
                      options={SCHEDULING_HOURS_OPTIONS}
                      className="form-control-select"
                      classNamePrefix="react-select"
                      placeholder="Working hours"
                      filterOption={null}
                      isDisabled={formData.off_due}
                      value={formData.hours}
                      onChange={(e) => setFormData((state) => ({ ...state, hours: e }))}
                    />
                  </div>
                  <div className="form-item">
                    <label className="form-label">Minutes</label>
                    <Select
                      isSearchable={false}
                      isDisabled={formData.off_due}
                      options={SCHEDULING_MINUTES_OPTIONS}
                      className="form-control-select"
                      classNamePrefix="react-select"
                      placeholder="Minutes"
                      filterOption={null}
                      value={formData.minutes}
                      onChange={(e) => setFormData((state) => ({ ...state, minutes: e }))}
                    />
                  </div>
                </div>

                {!assignment && renderColorFields()}
              </>
            )}
            {!!assignment && (
              <>
                <div className="form-item">
                  <div className="form-control-checkbox">
                    <input type="checkbox" checked={formData.off_due} onChange={handleChangeOffDueTo} />
                    <span className="form-label post-shift-label">Off due to </span>
                  </div>
                </div>
                {formData.off_due && (
                  <>
                    <div className="form-item">
                      <label className="form-label">Reason</label>
                      <SelectWithOtherOption
                        options={offDueToReasonOptions}
                        selectedOption={formData.off_due_reason}
                        setFormData={setFormData}
                        placeholder={'Reason'}
                        formKey={'off_due_reason'}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            {(!assignment || formData.off_due) && (!isCellFromPast || formData.post_shift) && (
              <div className="form-item">
                <div className="post-shift-to-staff-wrapper">
                  <div className="form-control-checkbox post-shift-to-staff-checkbox">
                    <input type="checkbox" disabled={isPastPeriod} checked={formData.post_shift} onChange={handlePostShiftToStaffChange} />
                    <span className="form-label post-shift-label">Post Shift to Staff</span>
                  </div>
                  {formData.post_shift && !formData.off_due && halfPart == 'both_halves' && (
                    <div className="post-shift-to-staff-select">
                      <Select
                        isSearchable={false}
                        isDisabled={formData.off_due}
                        options={POST_SHIFT_TO_STAFF_PART_OPTIONS}
                        className="form-control-select"
                        classNamePrefix="react-select"
                        filterOption={null}
                        value={formData.posted_part}
                        onChange={(e) => setFormData((state) => ({ ...state, posted_part: e }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {!!assignment && (formData.employeeFullName || formData.employee?.label) && renderColorFields()}
            {(formData.employeeFullName || formData.employee?.label) && (
              <div className="form-item">
                <label className="form-label">Notes (optional)</label>
                <input
                  name="notes"
                  type="text"
                  className="form-control"
                  placeholder="Enter"
                  value={formData.notes}
                  onChange={(e) => setFormData((state) => ({ ...state, notes: e.target.value }))}
                />
              </div>
            )}
            <div className="bottom-buttons">
              <button className="btn btn-outline" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {deleteEmployeeId ? (
        <DeleteEmployeeModal
          ref={deleteEmployeeModalRef}
          closeModal={() => setDeleteEmployeeId(null)}
          handleDeleteEmployee={handleDeleteEmployee}
        />
      ) : (
        <div ref={deleteEmployeeModalRef}></div>
      )}
    </Portal>
  );
};

export default CellAssignmentSidebar;
